import { DeleteOutlineRounded, EditOutlined, IosShare } from "@mui/icons-material";
import { Box, Button, Chip, Divider, Skeleton, Stack, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Chart, EditMetricModal, LineGraphSkeleton, LinkBehavior, MetricFeed, ShareButton } from "src/components";
import { downloadCsv, setTitlePrefix, useDestructiveConfirm } from "src/utilities";
import { Links } from "../../constants";
import { useDeleteMetric, useGetMetric } from "../../http";
import { MetricMapper } from "../../mapping";
import { DetailPageLayout } from "./DetailPageLayout";

interface UrlParams {
  metricGuid: string;
}

const MetricDetailsPage = () => {
  const { metricGuid } = useParams<keyof UrlParams>() as UrlParams;
  const { data: metric, isLoading } = useGetMetric(metricGuid);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { mutate: deleteMetric } = useDeleteMetric();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const confirm = useDestructiveConfirm();

  useEffect(() => {
    setTitlePrefix(metric?.title);
  }, [metric?.title]);

  function handleExportClicked() {
    if (metric === undefined) {
      return;
    }

    const csvData = MetricMapper.toCsvData(metric);

    downloadCsv({
      filename: `${DateTime.now().toISODate()} - ${metric.title}`,
      csv: csvData,
    });
  }

  async function handleDeleteClicked() {
    if (metric === undefined) {
      return;
    }

    const { confirmed } = await confirm({
      title: `Delete Metric`,
      content: `Are you sure you want to delete this metric?`,
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteMetric(
      {
        assignedTeamSlug: metric.assignedTeam.slug,
        guid: metric.guid,
      },
      {
        onSuccess: () => {
          if (metric !== undefined) {
            navigate(Links.TeamMetrics(metric.assignedTeam.slug));
            enqueueSnackbar(`Metric has been deleted`);
          }
        },
      },
    );
  }

  return (
    <DetailPageLayout
      title="Metric"
      actions={
        <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }}>
          <ShareButton variant="outlined" href={window.location.href} />
          <Button variant="outlined" startIcon={<IosShare />} onClick={handleExportClicked}>
            Export CSV
          </Button>
          {metric?.userCanEdit && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditOutlined />}
                onClick={() => {
                  setIsModalVisible(true);
                }}
              >
                Edit
              </Button>
              <Divider orientation="vertical" />
              <Button variant="outlined" color="error" onClick={handleDeleteClicked} sx={{ minWidth: 18 }}>
                <DeleteOutlineRounded />
              </Button>
            </>
          )}
        </Stack>
      }
    >
      {isLoading && (
        <Stack spacing={0.5} sx={{ px: { xs: 0.5, sm: 1 }, my: { xs: 0.5, sm: 1 } }}>
          <Skeleton variant="rectangular" height={24} width="10%" />
          <Skeleton variant="rectangular" height={30} width="50%" />
          <Box>
            <LineGraphSkeleton />
          </Box>
        </Stack>
      )}

      {!isLoading && metric && (
        <>
          <Stack spacing={{ xs: 0.5, sm: 1 }} sx={{ mt: { xs: 0.5, sm: 1 } }}>
            <Stack spacing={0.5} sx={{ px: { xs: 0.5, sm: 1 } }}>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  flexWrap: "wrap",
                }}
              >
                {metric.assignedTeam && (
                  <Chip
                    label={metric.assignedTeam.name}
                    color="primary"
                    component={LinkBehavior}
                    href={Links.Team(metric.assignedTeam.slug)}
                    clickable
                  />
                )}

                {metric.reportingUser && (
                  <Chip
                    label={metric.reportingUser.displayName}
                    color="person"
                    component={LinkBehavior}
                    href={Links.User(metric.reportingUser.userId)}
                    clickable
                  />
                )}
              </Stack>
              <Typography variant={"h4"}>{metric.title}</Typography>
              {metric.description && <Typography>{metric.description}</Typography>}
              {metric.tags.length > 0 && (
                <Stack
                  spacing={0.5}
                  direction="row"
                  sx={{
                    flexWrap: "wrap",
                  }}
                >
                  {metric.tags.map((tag) => (
                    <Chip key={tag} label={tag} />
                  ))}
                </Stack>
              )}
            </Stack>
            <Divider />
            {metric.datasets.length > 0 ? (
              <Stack spacing={0.5} sx={{ px: { xs: 0.5, sm: 1 }, minHeight: 300, justifyContent: "space-between" }}>
                <Chart
                  datasets={MetricMapper.toChartDataWithNulls(metric).map((data) => {
                    return {
                      title: data.title,
                      displayAsBar: data.displayAsBar,
                      color: data.color,
                      dotted: data.dotted,
                      data: data.chartData,
                      sortOrder: data.sortOrder,
                    };
                  })}
                  annotations={metric.annotations}
                  showXAxis
                  showYAxis
                  sx={{ height: { xs: "60vw", sm: "250px" } }}
                />
                {metric.dataLastModifiedDateTimeUtc && metric.dataLastModifiedByDisplayName && (
                  <Box>
                    <Typography variant="caption">{`Updated by ${
                      metric.dataLastModifiedByDisplayName
                    } on ${DateTime.fromISO(metric.dataLastModifiedDateTimeUtc, { zone: "utc" })
                      .toLocal()
                      .toLocaleString(DateTime.DATE_FULL)}`}</Typography>
                  </Box>
                )}
              </Stack>
            ) : (
              <Typography variant="placeholder" sx={{ px: { xs: 0.5, sm: 1 }, alignSelf: "center" }}>
                No data to display
              </Typography>
            )}
            <Box>
              <Divider />
              <MetricFeed metricGuid={metricGuid} />
            </Box>
          </Stack>

          <EditMetricModal
            isVisible={isModalVisible}
            metric={metric}
            onUpdated={() => setIsModalVisible(false)}
            onCancelled={() => setIsModalVisible(false)}
          />
        </>
      )}
    </DetailPageLayout>
  );
};

export { MetricDetailsPage };
